<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import type { ContentPreview } from "@/types";
import { type BodySource, transactionBodyDownloadUrl, useTransactionBody } from "@/queries/runs";
import { formatBytes } from "../utils/transactionFormatters";
import CodeBodyViewer from "./CodeBodyViewer.vue";

const props = defineProps<{
  /** The preview that came with the detail. Without one, the body is read as soon as this renders. */
  body?: ContentPreview;
  source: BodySource;
  runId: string;
  serviceId: string;
  transactionId: string;
}>();
const emit = defineEmits<{ loaded: [body: ContentPreview] }>();

const fetchBody = useTransactionBody();
const fetched = ref<ContentPreview | null>(null);
const loading = ref(false);
const failed = ref(false);
// Counts reads, so a read that answers after the source changed is dropped rather than shown.
let readNumber = 0;

// A parent builds the source inline, so it arrives as a new object on every render. Compare it by value.
const sourceKey = computed(() => JSON.stringify(props.source));

// Start over on a new source or preview. With no preview to offer, read the body at once.
watch(
  [sourceKey, () => props.body],
  () => {
    readNumber++;
    fetched.value = null;
    loading.value = false;
    failed.value = false;
    if (!props.body) read();
  },
  { immediate: true },
);

// What a read returned replaces the preview that offered it. A read may itself answer DownloadLinkOnly.
const shown = computed(() => fetched.value ?? props.body);

// Tell the parent what is shown once it is settled, so it can keep it or read its element count.
watch(
  shown,
  (body) => {
    if (body && body.outcome !== "Fetchable") emit("loaded", body);
  },
  { immediate: true },
);

const downloadUrl = computed(() =>
  transactionBodyDownloadUrl(props.runId, props.serviceId, props.transactionId, props.source),
);

async function read() {
  const thisRead = ++readNumber;
  loading.value = true;
  failed.value = false;
  try {
    const result = await fetchBody(props.runId, props.serviceId, props.transactionId, props.source);
    if (thisRead !== readNumber) return;
    fetched.value = result;
  } catch {
    if (thisRead !== readNumber) return;
    failed.value = true;
  } finally {
    if (thisRead === readNumber) loading.value = false;
  }
}
</script>

<template>
  <span v-if="shown?.outcome === 'None'" class="text-body-secondary">None</span>

  <span v-else-if="shown?.outcome === 'DownloadLinkOnly'">
    <a :href="downloadUrl" target="_blank" rel="noopener"><i class="bi bi-download me-1"></i>Download</a>
    <span class="text-body-secondary ms-1">{{ formatBytes(shown.lengthBytes) }}</span>
  </span>

  <CodeBodyViewer
    v-else-if="shown?.outcome === 'Included'"
    :body="shown.text ?? ''"
    :media-type="shown.mediaType"
    :declared-type="shown.type"
    :length-bytes="shown.lengthBytes"
    :download-url="downloadUrl"
  />

  <span v-else-if="failed">
    <span class="text-danger">Couldn't load the body.</span>
    <button type="button" class="btn btn-link btn-sm p-0 ms-1 align-baseline" @click="read">Retry</button>
  </span>

  <span v-else-if="loading" class="text-body-secondary">Loading…</span>

  <span v-else-if="shown">
    <button type="button" class="btn btn-link btn-sm p-0 align-baseline" @click="read">Show</button>
    <span class="text-body-secondary ms-1">{{ formatBytes(shown.lengthBytes) }}</span>
  </span>
</template>
